import { Box } from "@mui/material";
import { MilestoneTask } from "../../features/taskReports/MilestoneTask";
import { useMemo } from "react";

interface DeveloperTimeAllocationProps {
  task: MilestoneTask;
}

const colors = [
  "#9966ff", // Purple
  "#FF69B4", // Pink
  "#4CAF50", // Green
  "#FFEB3B", // Yellow
  "#F44336", // Red
  "#36A2EB", // light blue
  "#4361EE", // Royal Blue
  "#607D8B", // Blue Grey
];

const getRandomColor = (): string => {
  const randomColor = Math.floor(Math.random() * 16777215).toString(16);
  return `#${randomColor}`;
};

const DeveloperTimeAllocation = ({ task }: DeveloperTimeAllocationProps) => {
  const getDeveloperTimeBreakdown = (
    task: MilestoneTask,
    useCommaAndSpace: boolean = false
  ) => {
    if (!task.taskReports || task.taskReports.length === 0) {
      return "No time data available";
    }

    const developerTimeMap = new Map<string, number>();

    task.taskReports.forEach((report) => {
      if (report.developerName) {
        let developers: string[] = [];

        try {
          const parsedDevelopers = JSON.parse(report.developerName);
          if (Array.isArray(parsedDevelopers)) {
            developers = parsedDevelopers;
          } else {
            developers = [report.developerName];
          }
        } catch (error) {
          developers = [report.developerName];
        }

        const timePerDeveloper =
          report.totalTimeSpentOnTask / developers.length;

        developers.forEach((dev) => {
          const currentTime = developerTimeMap.get(dev) || 0;
          developerTimeMap.set(dev, currentTime + timePerDeveloper);
        });
      }
    });

    if (developerTimeMap.size === 0) {
      return "No developer data available";
    }

    const sortedDeveloperTimeEntries = Array.from(
      developerTimeMap.entries()
    ).sort(([, timeA], [, timeB]) => timeB - timeA);

    return sortedDeveloperTimeEntries
      .map(([developer, time]) => `${developer}: ${time}`)
      .join(useCommaAndSpace ? ", " : "\n");
  };

  const developerTimeBreakdown = getDeveloperTimeBreakdown(task);
  const totalTimeSpent = task.taskReports.reduce(
    (total, report) => total + (report.totalTimeSpentOnTask || 0),
    0
  );

  const allocations = useMemo(() => {
    return developerTimeBreakdown.split("\n").map((entry, index) => {
      const [name, time] = entry.split(": ");
      const timeSpent = parseFloat(time) || 0;
      const percentage =
        totalTimeSpent > 0 ? (timeSpent / totalTimeSpent) * 100 : 0;

      const color = index < colors.length ? colors[index] : getRandomColor();

      return { name, percentage, color };
    });
  }, [developerTimeBreakdown, totalTimeSpent]);

  return (
    <Box sx={{ display: "flex", width: "100%", borderRadius: "4px" }}>
      {allocations.map(({ name, percentage, color }, index) => (
        <Box
          key={index}
          sx={{
            width: `${percentage}%`,
            minWidth: percentage < 3 ? "3px" : "0",
            height: "5px",
            backgroundColor: color,
            borderTopLeftRadius: index === 0 ? "4px" : 0,
            borderBottomLeftRadius: index === 0 ? "4px" : 0,
            borderTopRightRadius: index === allocations.length - 1 ? "4px" : 0,
            borderBottomRightRadius:
              index === allocations.length - 1 ? "4px" : 0,
          }}
        />
      ))}
    </Box>
  );
};

export default DeveloperTimeAllocation;
